import React, { useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { 
  Box, 
  Typography, 
  Paper, 
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

/** Max characters per line for wrapped category labels (keeps radar plot larger). */
const CATEGORY_LABEL_MAX_CHARS = 18;

/**
 * Wrap a category label onto multiple lines at word boundaries.
 * Chart.js accepts string[] for multi-line point labels.
 */
const wrapCategoryLabel = (label: string, maxChars = CATEGORY_LABEL_MAX_CHARS): string | string[] => {
  if (!label || label.length <= maxChars) return label;

  const words = label.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxChars && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);

  return lines.length > 1 ? lines : label;
};

/** Normalise chart label (string or wrapped string[]) to lines for hit-testing. */
const getLabelLines = (label: string | string[] | undefined): string[] => {
  if (!label) return [];
  return Array.isArray(label) ? label : [label];
};

/**
 * Measure multi-line point-label bounds for click / hover hit testing.
 */
const getPointLabelBounds = (
  ctx: CanvasRenderingContext2D,
  item: { x: number; y: number; textAlign?: string; font?: { size?: number; family?: string } },
  label: string | string[],
  hitPadding = 10
) => {
  const lines = getLabelLines(label);
  const fontSize = item.font?.size || 12;
  const fontFamily = item.font?.family || 'Arial';
  ctx.font = `${fontSize}px ${fontFamily}`;

  const labelWidth = Math.max(...lines.map((line) => ctx.measureText(line).width), 0);
  const labelHeight = fontSize * Math.max(lines.length, 1);

  let left: number;
  let right: number;
  if (item.textAlign === 'right') {
    right = item.x;
    left = item.x - labelWidth;
  } else if (item.textAlign === 'left') {
    left = item.x;
    right = item.x + labelWidth;
  } else {
    left = item.x - labelWidth / 2;
    right = item.x + labelWidth / 2;
  }

  // Chart.js draws multi-line labels downward from item.y
  const top = item.y - fontSize;
  const bottom = item.y + labelHeight;

  return {
    left: left - hitPadding,
    right: right + hitPadding,
    top: top - hitPadding,
    bottom: bottom + hitPadding,
    lineCount: lines.length,
    fontSize,
  };
};

export interface RadarChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    pointBackgroundColor: string;
    pointBorderColor: string;
    pointHoverBackgroundColor: string;
    pointHoverBorderColor: string;
    site_name?: string;
  }>;
  site_name?: string;
  comparison_note?: string;
}

interface DetailedResult {
  question_text: string;
  answer_text: string;
  answer_score: number;
  category: string;
}

interface RadarChartProps {
  data: RadarChartData;
  title?: string;
  detailedResults?: DetailedResult[];
  priorYearDetailedResults?: DetailedResult[];
}

const RadarChart: React.FC<RadarChartProps> = ({ data, title, detailedResults, priorYearDetailedResults }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:899px)'); // Force update on resize

  // Outline-only series + wrapped labels so the radar plot can use more of the box
  const chartData = {
    labels: data.labels.map((label) => wrapCategoryLabel(label, isMobile ? 14 : CATEGORY_LABEL_MAX_CHARS)),
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: 'transparent',
      fill: false,
    })),
  };

  // Handle clicks on data points (dots) and category labels
  const handleChartClick = (event: any, elements: any[], chart: any) => {
    // First check if we clicked on a data point (dot)
    if (elements && elements.length > 0) {
      const element = elements[0];
      const index = element.index;
      const categoryLabel = data.labels[index];
      setSelectedCategory(categoryLabel);
      return;
    }

    // If no data point was clicked, check if we clicked on a category label
    const scale = chart.scales.r;
    if (!scale || !scale._pointLabelItems) return;

    // Get click coordinates relative to chart canvas
    const rect = chart.canvas.getBoundingClientRect();
    const x = event.native.clientX - rect.left;
    const y = event.native.clientY - rect.top;

    const ctx = chart.ctx;
    const pointLabelItems = scale._pointLabelItems;

    for (let i = 0; i < pointLabelItems.length; i++) {
      const item = pointLabelItems[i];
      const bounds = getPointLabelBounds(ctx, item, chart.data.labels[i]);

      if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
        setSelectedCategory(data.labels[i]);
        return;
      }
    }
  };

  // Get questions for selected category
  const selectedCategoryQuestions = selectedCategory && detailedResults
    ? detailedResults.filter(result => {
        const category = (result.category || '').trim();
        const selected = selectedCategory.trim();
        if (!category || !selected) return false;
        // Live API payloads use the same label as the radar axis.
        if (category === selected) return true;
        // Legacy mock data used "Level 1 - Level 2" while radar axes used one side.
        return category.endsWith(` - ${selected}`) || category.startsWith(`${selected} - `);
      })
    : [];

  // Get scores for selected category (current and prior year if available)
  const selectedCategoryIndex = selectedCategory ? data.labels.indexOf(selectedCategory) : -1;
  const selectedCategoryScore = selectedCategoryIndex >= 0 && data.datasets[0]
    ? data.datasets[0].data[selectedCategoryIndex] || 0
    : 0;
  const selectedCategoryPriorScore = selectedCategoryIndex >= 0 && data.datasets[1]
    ? data.datasets[1].data[selectedCategoryIndex] || 0
    : 0;

  // Plugin to handle cursor changes on hover over labels
  const cursorPlugin = {
    id: 'cursorPlugin',
    afterEvent: (chart: any, args: any) => {
      const event = args.event;
      if (event.type !== 'mousemove') return;

      const scale = chart.scales.r;
      if (!scale || !scale._pointLabelItems) return;

      const rect = chart.canvas.getBoundingClientRect();
      const x = event.native.clientX - rect.left;
      const y = event.native.clientY - rect.top;

      let overLabel = false;
      const ctx = chart.ctx;
      const pointLabelItems = scale._pointLabelItems;

      for (let i = 0; i < pointLabelItems.length; i++) {
        const item = pointLabelItems[i];
        const bounds = getPointLabelBounds(ctx, item, chart.data.labels[i]);

        if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
          overLabel = true;
          break;
        }
      }

      chart.canvas.style.cursor = overLabel ? 'pointer' : 'default';
    }
  };

  // Custom plugin to draw percentage labels below category labels (like landing page)
  const percentageLabelsPlugin = {
    id: 'percentageLabels',
    afterDraw: (chart: any) => {
      const ctx = chart.ctx;
      const scale = chart.scales.r;
      
      if (!scale || !scale._pointLabelItems) return;
      
      const pointLabelItems = scale._pointLabelItems;
      const fontSize = 12;
      
      // Draw percentages for each dataset below the category labels
      pointLabelItems.forEach((item: any, index: number) => {
        const x = item.x;
        const y = item.y;
        const lineCount = getLabelLines(chart.data.labels[index]).length;
        // Sit scores just under the wrapped category name
        const yOffset = 4 + lineCount * (fontSize + 2);
        
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        data.datasets.forEach((dataset, datasetIndex) => {
          const value = dataset.data[index];
          const text = `${Math.round(value)}%`;
          
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillStyle = dataset.borderColor;
          
          const lineHeight = 15;
          const yPosition = y + yOffset + (datasetIndex * lineHeight);
          
          ctx.shadowColor = isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          ctx.fillText(text, x, yPosition);
        });
        
        ctx.restore();
      });
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          color: isDarkMode ? '#FFFFFF' : '#212121',
          font: {
            size: 12,
            weight: 'normal' as const,
          },
          // Tight vertical spacing around legend items
          padding: 4,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: false, // Disable tooltips like landing page (percentages shown inline)
      },
      datalabels: {
        display: false, // Disable any datalabels plugin if present
      },
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: false,
    },
    hover: {
      mode: null as any, // Disable hover mode to prevent overlays
    },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        min: 0,
        max: 100,
        ticks: {
          display: false, // Hide the 20%, 40%, 60%, 80%, 100% labels for cleaner look
          stepSize: 20,
        },
        pointLabels: {
          font: {
            size: isMobile ? 11 : 12,
            weight: 'bold' as const,
          },
          color: '#1976d2', // Blue color to indicate clickability (works in both modes)
          // Closer labels = larger radar plot
          padding: isMobile ? 4 : 6,
        },
      },
    },
    elements: {
      point: {
        radius: 5,
        hoverRadius: 7,
        borderWidth: 2,
      },
      line: {
        borderWidth: 2.5,
      },
    },
    // Minimal canvas chrome so the radar can grow; wrapped labels need less side space
    layout: {
      padding: {
        top: 8,
        bottom: 28,
        left: 8,
        right: 8,
      },
    },
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
      {title && (
        <Typography variant="h6" align="center" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      )}
      {data.comparison_note && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 0.5 }}>
          {data.comparison_note}
        </Typography>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2, 
        minHeight: isMobile ? 'auto' : '560px',
      }}>
        {/* Radar Chart */}
        <Box sx={{ 
          flex: isMobile ? '1 1 100%' : '0 0 65%',
          minHeight: isMobile ? '420px' : '560px', 
          position: 'relative' 
        }}>
          <Radar data={chartData as any} options={options} plugins={[cursorPlugin, percentageLabelsPlugin]} />
        </Box>
        
        {/* Instruction Message - Desktop Only */}
        {!selectedCategory && !isMobile && (
          <Box sx={{ 
            display: 'flex',
            flex: '0 0 35%',
            alignItems: 'center',
            justifyContent: 'center',
            borderLeft: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
            pl: 2,
          }}>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ px: 2 }}>
              Click on a category label or data point to view detailed questions and scores
            </Typography>
          </Box>
        )}

        {/* Questions Detail Panel */}
        {selectedCategory && (
        <Box sx={{ 
          flex: isMobile ? '1 1 auto' : '0 0 35%',
          overflowY: 'auto',
          maxHeight: '560px',
          borderLeft: isMobile ? 'none' : `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
          borderTop: isMobile ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}` : 'none',
          pl: isMobile ? 0 : 2,
          pt: isMobile ? 2 : 0,
        }}>
            <Box>
              {/* Category Header */}
              <Box sx={{ 
                mb: 3, 
                pb: 2, 
                borderBottom: '2px solid',
                borderColor: isDarkMode ? 'rgba(229, 57, 53, 0.3)' : 'rgba(229, 57, 53, 0.2)',
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'text.primary',
                    mb: 1.5,
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {selectedCategory}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={`Score: ${Math.round(selectedCategoryScore)}%`}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      height: '36px',
                      backgroundColor: selectedCategoryScore >= 80 
                        ? 'rgba(76, 175, 80, 0.2)' 
                        : selectedCategoryScore >= 50 
                        ? 'rgba(255, 152, 0, 0.2)' 
                        : 'rgba(244, 67, 54, 0.2)',
                      color: selectedCategoryScore >= 80 
                        ? '#4caf50' 
                        : selectedCategoryScore >= 50 
                        ? '#ff9800' 
                        : '#f44336',
                      border: '2px solid',
                      borderColor: selectedCategoryScore >= 80 
                        ? '#4caf50' 
                        : selectedCategoryScore >= 50 
                        ? '#ff9800' 
                        : '#f44336',
                    }}
                  />
                  {data.datasets.length > 1 && selectedCategoryPriorScore > 0 && (
                    <Chip
                      icon={selectedCategoryScore >= selectedCategoryPriorScore ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      label={`${Math.abs(Math.round(selectedCategoryScore - selectedCategoryPriorScore))}% vs Last Year`}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        height: '36px',
                        backgroundColor: selectedCategoryScore >= selectedCategoryPriorScore 
                          ? 'rgba(76, 175, 80, 0.15)' 
                          : 'rgba(244, 67, 54, 0.15)',
                        color: selectedCategoryScore >= selectedCategoryPriorScore ? '#4caf50' : '#f44336',
                        '& .MuiChip-icon': {
                          color: selectedCategoryScore >= selectedCategoryPriorScore ? '#4caf50' : '#f44336',
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Questions List */}
              {selectedCategoryQuestions.length > 0 ? (
                <Box sx={{ maxHeight: '420px', overflow: 'auto', pr: 1 }}>
                  {selectedCategoryQuestions.map((result, index) => {
                    // Find matching prior year question
                    const priorYearResult = priorYearDetailedResults?.find(pr => 
                      pr.question_text === result.question_text && 
                      pr.category === result.category
                    );
                    const priorScore = priorYearResult?.answer_score || 0;
                    const scoreDiff = result.answer_score - priorScore;
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2.5,
                          pb: 2.5,
                          borderBottom: index < selectedCategoryQuestions.length - 1 
                            ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'}` 
                            : 'none',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          pl: 2,
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '3px',
                            backgroundColor: result.answer_score >= 80 
                              ? '#4caf50' 
                              : result.answer_score >= 50 
                              ? '#ff9800' 
                              : '#f44336',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                          '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                            transform: 'translateX(4px)',
                            '&:before': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {/* Question */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 0.75,
                            lineHeight: 1.5,
                            fontSize: '0.95rem',
                          }}
                        >
                          {result.question_text}
                        </Typography>
                        
                        {/* Answer */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            lineHeight: 1.5,
                            mb: 1,
                            fontSize: '0.9rem',
                          }}
                        >
                          {result.answer_text}
                        </Typography>
                        
                        {/* Score and Change */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={`${result.answer_score.toFixed(0)}%`}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.85rem',
                              backgroundColor: result.answer_score >= 80 
                                ? 'rgba(76, 175, 80, 0.2)' 
                                : result.answer_score >= 50 
                                ? 'rgba(255, 152, 0, 0.2)' 
                                : 'rgba(244, 67, 54, 0.2)',
                              color: result.answer_score >= 80 
                                ? '#4caf50' 
                                : result.answer_score >= 50 
                                ? '#ff9800' 
                                : '#f44336',
                              border: '1px solid',
                              borderColor: result.answer_score >= 80 
                                ? '#4caf50' 
                                : result.answer_score >= 50 
                                ? '#ff9800' 
                                : '#f44336',
                            }}
                          />
                          
                          {priorYearResult && scoreDiff !== 0 && (
                            <Chip
                              icon={scoreDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                              label={`${Math.abs(scoreDiff).toFixed(0)}%`}
                              size="small"
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: scoreDiff > 0 
                                  ? 'rgba(76, 175, 80, 0.1)' 
                                  : 'rgba(244, 67, 54, 0.1)',
                                color: scoreDiff > 0 ? '#4caf50' : '#f44336',
                                '& .MuiChip-icon': {
                                  color: scoreDiff > 0 ? '#4caf50' : '#f44336',
                                  fontSize: '16px',
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No detailed questions available for this category.
                </Typography>
              )}
            </Box>
        </Box>
        )}
      </Box>
    </Paper>
  );
};

export default RadarChart; 